import {
  AppBskyFeedLike,
  AppBskyFeedPost,
  AppBskyFeedRepost,
} from '@atproto/api'
import { db } from '../cache/db'

export type AnomalyReport = {
  handle: string
  totalActions: number
  posts: number
  replies: number
  likes: number
  reposts: number
  /** Hour-of-day (UTC) bucket counts, length 24 */
  hourBuckets: number[]
  /** Number of inter-arrival gaps under 1000 ms (consecutive actions) */
  subSecondBursts: number
  /** Number of inter-arrival gaps under 60_000 ms */
  subMinuteBursts: number
  /** Action timestamps at exactly *.000ms — almost always means scripted/CRON-style scheduling */
  zeroMsTimestamps: number
  /** Median inter-arrival gap, ms */
  medianGapMs: number
  /** Mean gap */
  meanGapMs: number
  /** Earliest createdAt seen */
  earliest: string | null
  /** Most recent */
  latest: string | null
}

export async function accountAnomalies(handle: string): Promise<AnomalyReport> {
  const h = handle.toLowerCase()
  const [posts, likes, reposts] = await Promise.all([
    db.records.where('[handle+collection]').equals([h, 'app.bsky.feed.post']).toArray(),
    db.records.where('[handle+collection]').equals([h, 'app.bsky.feed.like']).toArray(),
    db.records.where('[handle+collection]').equals([h, 'app.bsky.feed.repost']).toArray(),
  ])

  const ts: number[] = []
  let postCount = 0
  let replyCount = 0

  for (const p of posts) {
    if (!AppBskyFeedPost.isRecord(p.value)) continue
    const t = new Date(p.value.createdAt).getTime()
    if (Number.isNaN(t)) continue
    if (p.value.reply?.parent) replyCount++
    else postCount++
    ts.push(t)
  }
  for (const l of likes) {
    if (!AppBskyFeedLike.isRecord(l.value)) continue
    const t = new Date(l.value.createdAt).getTime()
    if (!Number.isNaN(t)) ts.push(t)
  }
  for (const r of reposts) {
    if (!AppBskyFeedRepost.isRecord(r.value)) continue
    const t = new Date(r.value.createdAt).getTime()
    if (!Number.isNaN(t)) ts.push(t)
  }

  ts.sort((a, b) => a - b)

  const hourBuckets = new Array(24).fill(0) as number[]
  let zeroMs = 0
  for (const t of ts) {
    hourBuckets[new Date(t).getUTCHours()]++
    if (t % 1000 === 0) zeroMs++
  }

  const gaps: number[] = []
  for (let i = 1; i < ts.length; i++) gaps.push(ts[i] - ts[i - 1])
  gaps.sort((a, b) => a - b)
  const median = gaps.length === 0 ? 0 : gaps[Math.floor(gaps.length / 2)]
  const mean = gaps.length === 0 ? 0 : gaps.reduce((s, g) => s + g, 0) / gaps.length
  const subSecond = gaps.filter((g) => g < 1000).length
  const subMinute = gaps.filter((g) => g < 60_000).length

  return {
    handle: h,
    totalActions: ts.length,
    posts: postCount,
    replies: replyCount,
    likes: likes.length,
    reposts: reposts.length,
    hourBuckets,
    subSecondBursts: subSecond,
    subMinuteBursts: subMinute,
    zeroMsTimestamps: zeroMs,
    medianGapMs: Math.round(median),
    meanGapMs: Math.round(mean),
    earliest: ts.length > 0 ? new Date(ts[0]).toISOString() : null,
    latest: ts.length > 0 ? new Date(ts[ts.length - 1]).toISOString() : null,
  }
}

export type ClusterEvent = {
  actorHandle: string
  ts: number
  verb: 'like' | 'repost'
}

export type Cluster = {
  subjectUri: string
  events: ClusterEvent[]
  /** Distinct accounts in cluster */
  actorCount: number
  /** Window span (last - first), ms */
  spanMs: number
  firstAt: string
  lastAt: string
  /** Suspicion score: more accounts in less time = higher */
  score: number
}

export type ClusterScanOptions = {
  epsilonMs: number
  minAccounts: number
  /** If set, only consider events where the subject URI host (DID) matches this DID */
  targetDid?: string
}

/**
 * Scan every cached like + repost record across every handle in IDB. Group
 * events by subject URI, slide a window of `epsilonMs` and report any group
 * where ≥`minAccounts` distinct actors interacted within that window.
 */
export async function clusterScan(opts: ClusterScanOptions): Promise<Cluster[]> {
  const all: Array<ClusterEvent & { subject: string }> = []
  await db.records.toCollection().each((r) => {
    if (r.collection === 'app.bsky.feed.like' && AppBskyFeedLike.isRecord(r.value)) {
      const ts = new Date(r.value.createdAt).getTime()
      if (Number.isNaN(ts)) return
      const subject = r.value.subject?.uri
      if (!subject) return
      if (opts.targetDid && !subject.startsWith(`at://${opts.targetDid}/`)) return
      all.push({ subject, ts, actorHandle: r.handle, verb: 'like' })
    } else if (r.collection === 'app.bsky.feed.repost' && AppBskyFeedRepost.isRecord(r.value)) {
      const ts = new Date(r.value.createdAt).getTime()
      if (Number.isNaN(ts)) return
      const subject = r.value.subject?.uri
      if (!subject) return
      if (opts.targetDid && !subject.startsWith(`at://${opts.targetDid}/`)) return
      all.push({ subject, ts, actorHandle: r.handle, verb: 'repost' })
    }
  })

  const bySubject = new Map<string, Array<ClusterEvent & { subject: string }>>()
  for (const e of all) {
    let arr = bySubject.get(e.subject)
    if (!arr) {
      arr = []
      bySubject.set(e.subject, arr)
    }
    arr.push(e)
  }

  const clusters: Cluster[] = []
  for (const [subject, evs] of bySubject) {
    if (evs.length < opts.minAccounts) continue
    evs.sort((a, b) => a.ts - b.ts)
    // Sliding window
    for (let i = 0; i < evs.length; i++) {
      const j0 = i
      const actors = new Set<string>()
      let j = i
      while (j < evs.length && evs[j].ts - evs[i].ts <= opts.epsilonMs) {
        actors.add(evs[j].actorHandle)
        j++
      }
      if (actors.size >= opts.minAccounts) {
        const window = evs.slice(j0, j)
        const span = window[window.length - 1].ts - window[0].ts
        clusters.push({
          subjectUri: subject,
          events: window.map((e) => ({ actorHandle: e.actorHandle, ts: e.ts, verb: e.verb })),
          actorCount: actors.size,
          spanMs: span,
          firstAt: new Date(window[0].ts).toISOString(),
          lastAt: new Date(window[window.length - 1].ts).toISOString(),
          // Higher = more accounts piled into a tighter window
          score: actors.size / Math.max(1, span / 1000),
        })
        i = j - 1 // skip past this window
      }
    }
  }

  clusters.sort((a, b) => b.score - a.score)
  return clusters
}
